// @ts-nocheck

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { __ } from '../../../../i18n/l10n';
import { GRPrimaryButtonBackroundImgPath } from '../../assets/img';
import useGRHomeContext from '../../hooks/useGRHomeContext';
import { GRFullScreenNotifications } from '../../types/notification';

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  background: radial-gradient(transparent, #00000099);

  &.gr-round-failed-notification-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-round-failed-notification-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }

  ${({ $show }) =>
    !$show
      ? css`
          pointer-events: none;
        `
      : undefined}
`;

const Labels = styled.div`
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  margin-top: -10%;
  filter: drop-shadow(0 0 0.3em #00000077);
`;

const FailedLabel = styled.div`
  font-size: 4em;
`;

const RestartRoundButton = styled.div`
  position: absolute;
  white-space: nowrap;
  bottom: 1em;
  left: 50%;
  transform: translateX(-50%);
  font-size: 135%;

  padding: 2em 4.5em !important;

  transition: none 150ms ease-in-out;
  transition-property: transform, filter;
  background-image: url(${GRPrimaryButtonBackroundImgPath});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;

  user-select: none;

  &.gr-restart-round-button-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-restart-round-button-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }

  ${({ $disabled }) => css`
    ${$disabled
      ? css`
          filter: grayscale(1);
          pointer-events: none;
        `
      : css`
          cursor: pointer;

          &:active {
            transform: translateX(-50%) scale(0.95);
          }

          ${!Target.__MOBILE__
            ? css`
                &:hover {
                  filter: brightness(110%);
                }
              `
            : undefined}
        `}
  `}

  ${({ $show }) =>
    !$show
      ? css`
          pointer-events: none;
        `
      : undefined}
`;

type GRRoundFailedNotificationProps = {
  className?: string;
  onRestartRoundClick: () => void;
};

const GRRoundFailedNotification = ({ className = '', onRestartRoundClick }: GRRoundFailedNotificationProps) => {
  const { fullScreenNotifShown } = useGRHomeContext();
  const show = useMemo(() => fullScreenNotifShown === GRFullScreenNotifications.ROUND_FAILED, [fullScreenNotifShown]);

  const totalFreebetsAmountTimeoutIdRef = useRef();
  const [isFinal, setIsFinal] = useState(false);

  useEffect(() => {
    if (show && !totalFreebetsAmountTimeoutIdRef.current) {
      totalFreebetsAmountTimeoutIdRef.current = setTimeout(() => {
        setIsFinal(true);
      }, 0);
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      return () => {
        const totalFreebetsAmountTimeoutId = totalFreebetsAmountTimeoutIdRef.current;

        if (totalFreebetsAmountTimeoutId) {
          clearTimeout(totalFreebetsAmountTimeoutId);
        }
      };
    }
  }, [show]);

  return (
    <CSSTransition
      in={show}
      classNames="gr-round-failed-notification-appearance-transition"
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <Wrapper className={className} $show={show}>
        <Labels>
          <FailedLabel>{__('You lose')}</FailedLabel>
        </Labels>
        <CSSTransition
          in={isFinal}
          classNames="gr-restart-round-button-appearance-transition"
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          <RestartRoundButton $show={isFinal} onClick={onRestartRoundClick}>
            {__('Start again')}
          </RestartRoundButton>
        </CSSTransition>
      </Wrapper>
    </CSSTransition>
  );
};

export default GRRoundFailedNotification;
